import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { api, formatError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Crosshair } from "@/components/Crosshair";
import { PayoutBreakdown } from "@/components/PayoutBreakdown";
import {
  CATEGORIES, TOP_LEVEL_CATEGORIES, CONDITIONS, LISTING_TYPES, MAG_RANGES, RETICLES, MOUNTS, SHIPPING, US_STATES,
  TUBE_DIAMETERS, FOCAL_PLANES, WTB_TIMEFRAMES,
  LASER_TYPES, LASER_WAVELENGTHS, LASER_BATTERIES, laserIsIrCapable, COLORS,
  KNIFE_SPECS, KNIVES_CATEGORY, DEVICE_TYPES, SENSOR_RESOLUTIONS, listingPath,
} from "@/lib/constants";
import {
  PART_SPECS, isPartCategory, partGroupOf, partLeafLabel, isPartParent, leavesForParentLabel, parentLabelForLeaf, groupToParentLabel, visibleFieldsFor, detectProhibited, isFirearmPartCategory, AMMO_MANUFACTURERS,
} from "@/lib/partCategories";
import { Upload, AlertTriangle, Loader2, MailWarning, CreditCard, ShieldCheck, Lock, ArrowRight, ArrowLeft, CheckCircle2, Package, Truck, Ban, Repeat, Save, Camera, Trash2, Plus } from "lucide-react";
import { toast } from "@/lib/toast";
import VerifyIdFlow from "@/components/verify/VerifyIdFlow";
import { ListingPhotoUploader } from "@/components/ListingPhotoUploader";
import { celebrate } from "@/lib/celebrate";
import { CatalogIdentifier } from "@/components/CatalogIdentifier";

const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors font-body";
const labelCls = "font-mono text-xs uppercase tracking-wide text-secondary-foreground block mb-1";

// MAP / anti-diversion: a brand-NEW listing is forbidden in NV/Thermal (mirrors the server 400).
// Only "New" is barred — "Like New" (a used-condition tier) stays available.
const NV_THERMAL_CAT = "Night Vision & Thermal Optics";
const LASERS_CAT = "Lasers";

// The "third state" marker: a field whose value came from the catalog (not a placeholder hint,
// not something the seller typed). Rendered ONLY while the value is still catalog-owned — the
// moment the seller edits the field the mark is removed by `set()`.
const CatalogMark = ({ k }) => (
  <span data-testid={`catalog-mark-${k}`} className="ml-2 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide text-olive-bright border border-olive/50 bg-olive/10 px-1.5 py-0.5 align-middle">
    <CheckCircle2 className="w-2.5 h-2.5" /> catalog
  </span>
);

// One structured spec field for the new part categories (Barrels/Chassis/Uppers/Sights/Triggers).
// `prefix` namespaces the data-testid so the same renderer serves parts (part-spec-*) and knives (knife-spec-*).
function PartSpecField({ f, value, onChange, prefix = "part" }) {
  const base = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors font-body";
  const lbl = (
    <label className={labelCls}>{f.label}{f.required && <span className="text-blaze"> *</span>}</label>
  );
  if (f.type === "bool") {
    return (
      <label className="flex items-center gap-2 cursor-pointer py-2" data-testid={`${prefix}-spec-${f.key}`}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-olive w-4 h-4" />
        <span className="font-mono text-xs uppercase text-secondary-foreground">{f.label}</span>
      </label>
    );
  }
  if (f.type === "select") {
    return (<div>{lbl}<select value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`${prefix}-spec-${f.key}`}><option value="">—</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>);
  }
  if (f.type === "suggest") {
    const listId = `sugg-${f.key}`;
    return (<div>{lbl}<input list={listId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.hint || ""} className={base} data-testid={`${prefix}-spec-${f.key}`} /><datalist id={listId}>{f.suggest.map((o) => <option key={o} value={o} />)}</datalist></div>);
  }
  return (<div>{lbl}<input type={f.type === "number" ? "number" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.hint || ""} className={base} data-testid={`${prefix}-spec-${f.key}`} /></div>);
}

function PartSpecs({ group, specs, setSpec, leaf }) {
  const fields = visibleFieldsFor(group, specs, leaf);
  if (!fields.length) return null;
  return (
    <div className="space-y-3" data-testid="create-part-specs">
      <div className="font-mono text-[11px] uppercase tracking-wide text-olive-bright">Part details</div>
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <PartSpecField key={f.key} f={f} value={specs?.[f.key]} onChange={(v) => setSpec(f.key, v)} />
        ))}
      </div>
    </div>
  );
}

// Structured spec fields for the "Knives & Blades" category — reuses the generic spec-field
// renderer (knife-spec-* testids). All optional; values persist in listing.knife_specs.
function KnifeSpecs({ specs, setSpec }) {
  return (
    <div className="space-y-3" data-testid="create-knife-specs">
      <div className="font-mono text-[11px] uppercase tracking-wide text-olive-bright">Blade details</div>
      <div className="grid sm:grid-cols-2 gap-4">
        {KNIFE_SPECS.map((f) => (
          <PartSpecField key={f.key} f={f} prefix="knife" value={specs?.[f.key]} onChange={(v) => setSpec(f.key, v)} />
        ))}
      </div>
    </div>
  );
}

// A part parent with exactly ONE leaf (e.g. Magazines) auto-selects that leaf so the seller never
// sees a pointless one-option "type" menu. Multi-leaf parents still require an explicit pick.
const autoLeafForParent = (label) => {
  const leaves = leavesForParentLabel(label);
  return leaves.length === 1 ? leaves[0].id : "";
};

const INIT = {
  title: "", description: "", category: "Rifle Scopes", category_top: "Rifle Scopes", brand: "", model: "",
  condition: "Used - Good", listing_type: "sell", price: "", open_to_trades: false,
  trade_preferences: "", magnification: "", mag_range: "", reticle_type: "",
  mount_type: "", tube_diameter: "", mag_min: "", mag_max: "", focal_plane: "",
  weight_oz: "", wt_lb: "", wt_oz: "",
  // Provenance (optional). Age is a purchase date OR a free-text approximation (age_mode picks one).
  age_mode: "date", purchase_date: "", approx_age: "", warranty_remaining: "",
  // Night Vision & Thermal structured fields (only sent for that category).
  device_type: "", sensor_resolution: "", detection_range: "",
  wtb_conditions: [], wtb_timeframe: "",
  shipping: "both", city: "", state: "", zip_code: "",
  // Structured spec values for the NEW part categories (keyed by field key). {} for optics.
  part_specs: {}, uppers_attest: false,
  // Structured spec values for the "Knives & Blades" category (keyed by field key). {} otherwise.
  knife_specs: {},
  // Shipping fulfillment path + package (only meaningful for a shippable sell listing).
  ship_mode: "buy_label", package_preset: "",
  pkg_length_in: "", pkg_width_in: "", pkg_height_in: "", pkg_weight_lb: "", pkg_weight_oz: "",
  flat_shipping: "", free_shipping: false,
  photos: [], video: "", itar: false, requires_ffl: false, optic_type: "", attestation: false,
  // Lasers category
  laser_type: "", laser_wavelength: "", laser_adjustable: false, laser_battery: "",
  color: "",
  // Best Offer — fixed-price sell listings only. accept_offers gate + optional thresholds.
  accept_offers: false, offer_min: "", offer_auto_accept: "",
  // Variants — sell listings only; each {label, price, quantity, condition, vid?}.
  variants: [],
};

// Which structured spec fields apply per category — never render a control that can't mean
// anything for the item (a red dot has no focal plane; a rangefinder has no reticle).
const OPTIC_SPEC = {
  "Rifle Scopes": { magMinMax: 1, magRange: 1, reticle: 1, tube: 1, focal: 1, weight: 1, mount: 1, magLabel: 1 },
  "Prism Scopes & Magnifiers": { magMinMax: 1, reticle: 1, tube: 1, weight: 1, mount: 1, magLabel: 1 },
  "Red Dots & Reflex Sights": { reticle: 1, mount: 1, weight: 1 },
  "Night Vision & Thermal Optics": { opticType: 1, thermal: 1, magMinMax: 1, weight: 1, mount: 1, magLabel: 1 },
  "Lasers": { laser: 1, mount: 1, weight: 1 },
  "Binoculars & Spotting Scopes": { magMinMax: 1, weight: 1, magLabel: 1 },
  "Rangefinders": { magMinMax: 1, weight: 1 },
  "Mounts, Rings & Bases": { mount: 1, tube: 1, weight: 1 },
  "Parts & Accessories": { weight: 1 },
  "Knives & Blades": { weight: 1 },
  "Services": {},
};
const specFields = (cat) => OPTIC_SPEC[cat] || { weight: 1 };

// 3-step "finish setting up to get paid" card for /create. Shows the account's REAL progress
// (email · ID · payout); completed steps stay visible as Done rather than vanishing. The payout
// step routes to the working /settings/payouts page. Money math beneath makes "add payout" concrete.
// The whole card disappears once all three are done — it is onboarding, not furniture.
function OnboardingSteps({ user, payout, quote, resending, onResendEmail, onVerifyId }) {
  const emailDone = user?.email_verified !== false;
  const idDone = !!user?.identity_verified;
  const payoutActive = !!(payout && (payout.payout_enabled || payout.payout_chip === "enabled"));
  const payoutReconnect = !!(payout && payout.reconnect_required);
  const steps = [
    { id: "email", label: "Verify your email", unlocks: "Confirms your account before you can publish.", done: emailDone },
    { id: "id", label: "Verify your ID", unlocks: "A two-minute Stripe check — required before your listing goes live.", done: idDone },
    { id: "payout", label: "Add payout", unlocks: "Connect Stripe so sale funds reach you. Held until the buyer confirms delivery.", done: payoutActive, reconnect: payoutReconnect },
  ];
  if (emailDone && idDone && payoutActive) return null;
  const doneCount = steps.filter((s) => s.done).length;
  const currentId = steps.find((s) => !s.done)?.id;

  const whole = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const cents = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const receive = quote ? Math.max(0, quote.price - quote.fee) : null;

  const cta = (s) => {
    if (s.id === "email") return (
      <button type="button" onClick={onResendEmail} disabled={resending} data-testid="onboard-cta-email"
        className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-[12px] px-3.5 py-2 transition-colors whitespace-nowrap">
        {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailWarning className="w-3.5 h-3.5" />} Resend email
      </button>);
    if (s.id === "id") return (
      <button type="button" onClick={onVerifyId} data-testid="onboard-cta-id"
        className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-[12px] px-3.5 py-2 transition-colors whitespace-nowrap">
        <ShieldCheck className="w-3.5 h-3.5" /> Verify my ID
      </button>);
    return (
      <Link to="/settings/payouts" data-testid="onboard-cta-payout"
        className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-[12px] px-3.5 py-2 transition-colors whitespace-nowrap">
        <CreditCard className="w-3.5 h-3.5" /> {s.reconnect ? "Reconnect payouts" : "Set up payouts"}
      </Link>);
  };

  return (
    <div className="mb-5 border border-sniper bg-surface p-5" data-testid="create-onboarding-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-bold text-lg uppercase tracking-tight text-foreground">Finish setting up to get paid</h3>
        <span className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground" data-testid="onboard-progress">{doneCount} of 3 complete</span>
      </div>
      <div className="mt-4 space-y-2.5">
        {steps.map((s) => {
          const isCurrent = s.id === currentId;
          return (
            <div key={s.id} data-testid={`onboard-step-${s.id}`}
              className={`flex items-start gap-3 border p-3 ${isCurrent ? "border-olive/60 bg-olive/5" : "border-sniper bg-obsidian/40"}`}>
              <span className="mt-0.5 shrink-0">
                {s.done
                  ? <CheckCircle2 className="w-4 h-4 text-olive-bright" strokeWidth={1.8} />
                  : <span className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-full border ${isCurrent ? "border-olive-bright" : "border-sniper"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isCurrent ? "bg-olive-bright" : "bg-muted-foreground"}`} />
                    </span>}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold text-[14px] uppercase tracking-tight" style={{ color: s.done ? "#8B8D90" : "#DAD6CE" }}>{s.label}</p>
                <p className="text-[12px] font-body text-secondary-foreground mt-0.5">{s.unlocks}</p>
              </div>
              <div className="shrink-0">
                {s.done
                  ? <span data-testid={`onboard-chip-${s.id}`} className="inline-flex font-mono text-[10px] uppercase px-2 py-1 border border-olive/50 text-olive-bright">Done</span>
                  : (isCurrent || s.reconnect) ? cta(s)
                  : <span data-testid={`onboard-chip-${s.id}`} className="inline-flex font-mono text-[10px] uppercase px-2 py-1 border border-sniper text-muted-foreground">Not started</span>}
              </div>
            </div>
          );
        })}
      </div>
      {quote && quote.price > 0 && (
        <div className="mt-4 border border-volt/30 bg-volt/5 p-4" data-testid="onboard-money-math">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">What you take home on a {whole(quote.price)} sale</p>
          <dl className="space-y-1.5 font-mono text-sm">
            <div className="flex items-center justify-between"><dt className="text-secondary-foreground">Sale price</dt><dd className="text-foreground">{whole(quote.price)}</dd></div>
            <div className="flex items-center justify-between"><dt className="text-secondary-foreground">Platform fee <span className="text-muted-foreground">({quote.plan_label})</span></dt><dd className="text-blaze">−${cents(quote.fee)}</dd></div>
            <div className="flex items-center justify-between border-t border-sniper pt-1.5"><dt className="text-foreground font-bold">You receive</dt><dd className="text-olive-bright font-bold text-base" data-testid="onboard-you-receive">{whole(receive)}</dd></div>
          </dl>
          <p className="text-[11px] font-body text-muted-foreground mt-2">Card processing is separate, charged at cost when the item sells.</p>
        </div>
      )}
    </div>
  );
}

// Seller listing-milestone celebration — confetti + a warm toast when a publish crosses a
// Listings badge tier (1, 10, 25, 50, 100, 250, 500). Fired right after a successful publish.
function celebrateMilestone(n) {
  celebrate();
  const msg = n === 1
    ? "Your first listing is live — welcome to the marketplace!"
    : `Milestone unlocked: ${n} listings published! Nicely done.`;
  toast.success(msg, { duration: 6000 });
}


export default function CreateListing() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from; // set when opened from the admin header + POST
  const { user, refresh, platformLabelsEnabled } = useAuth();
  const [form, setForm] = useState(INIT);
  const [quota, setQuota] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [onboardQuote, setOnboardQuote] = useState(null);
  const [fees, setFees] = useState(null);
  const [feePreview, setFeePreview] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [sharePreviewUrl, setSharePreviewUrl] = useState("");
  const [catalog, setCatalog] = useState(null);        // listable resolve payload (drives the summary card)
  const [catalogBlock, setCatalogBlock] = useState(null); // {message, brand, model} — refuse by name, blocks publish
  const [catalogMiss, setCatalogMiss] = useState(null);   // identifier that didn't match — drives the "fill in manually" note
  const [prefilled, setPrefilled] = useState(() => new Set()); // field keys still owned by the catalog
  const [specsCollapsed, setSpecsCollapsed] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const draftLoadedRef = useRef(false);
  const packageRef = useRef(null);

  const emailUnverified = user && user.email_verified === false;
  // HARD GATE: verification required to list (config-flagged). Mirrors the server-side 403.
  const idGate = !editing && !!user && !user.identity_verified;

  const resendVerification = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/auth/send-verification");
      if (data.status === "already_verified") { toast.success("Your email is already verified."); refresh(); }
      else toast.success("Verification email sent — check your inbox.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    api.get("/quota").then(({ data }) => setQuota(data)).catch(() => {});
    api.get("/dealer/stripe/status").then(({ data }) => setPayoutStatus(data)).catch(() => {});
    api.get("/config/package-presets").then(({ data }) => setPresets(data.presets || (Array.isArray(data) ? data : []))).catch(() => {});
    api.get("/config/fees").then(({ data }) => setFees(data)).catch(() => {});
    api.get("/config/tiers").then(({ data }) => setTiers(data.tiers || [])).catch(() => {});
    api.get("/config/brands").then(({ data }) => setBrandList(data.brands || [])).catch(() => {});
    if (editing) {
      api.get(`/listings/${id}`).then(({ data }) => {
        const clean = {};
        Object.keys(INIT).forEach((k) => { clean[k] = data[k] ?? INIT[k]; });
        // Derive the top-level selection from the stored category (leaf id -> parent label).
        clean.category_top = isPartCategory(clean.category) ? (parentLabelForLeaf(clean.category) || clean.category) : clean.category;
        // Split stored ounces back into LB + OZ for the spec-weight inputs.
        const woz = Number(data.weight_oz);
        if (Number.isFinite(woz) && woz > 0) { clean.wt_lb = String(Math.floor(woz / 16) || ""); clean.wt_oz = String(+(woz % 16).toFixed(1) || ""); }
        // Rehydrate the package + shipping path from the stored listing.
        clean.ship_mode = data.ship_mode || (data.package ? "buy_label" : (data.flat_shipping_cents != null ? "own_label" : "buy_label"));
        if (data.package) {
          clean.package_preset = data.package.preset && data.package.preset !== "custom" ? data.package.preset : "";
          clean.pkg_length_in = data.package.length_in ?? ""; clean.pkg_width_in = data.package.width_in ?? ""; clean.pkg_height_in = data.package.height_in ?? "";
          const plb = Number(data.package.weight_lb);
          if (Number.isFinite(plb)) { clean.pkg_weight_lb = String(Math.floor(plb) || ""); clean.pkg_weight_oz = String(+(((plb % 1) * 16)).toFixed(1) || ""); }
        }
        if (data.flat_shipping_cents != null) clean.flat_shipping = (data.flat_shipping_cents / 100).toString();
        clean.free_shipping = !!data.free_shipping;
        // Best Offer thresholds are owner-only on the API; GET /listings/{id} restores the raw
        // offer_min / offer_auto_accept for the owner so the edit form prefills (never silently wipes).
        clean.accept_offers = !!data.accept_offers;
        clean.offer_min = data.offer_min ?? "";
        clean.offer_auto_accept = data.offer_auto_accept ?? "";
        // Age: pick the tab that matches whatever was stored (approx only if no purchase date).
        clean.age_mode = (!data.purchase_date && data.approx_age) ? "approx" : "date";
        setForm({ ...clean, price: data.price || "" });
      }).catch(() => {});
    }
  }, [id, editing]);

  // Pre-select a top-level category when arriving from an empty-category CTA (/create?cat=Barrels).
  // A part parent seeds category_top only (seller still picks the leaf); an optic seeds both.
  useEffect(() => {
    if (editing) return;
    const params = new URLSearchParams(location.search);
    if (params.get("draft")) return; // a draft load sets the category itself
    const cat = params.get("cat");
    if (!cat || !TOP_LEVEL_CATEGORIES.includes(cat)) return;
    setForm((f) => isPartParent(cat)
      ? { ...f, category_top: cat, category: autoLeafForParent(cat), part_specs: {}, uppers_attest: false }
      : { ...f, category_top: cat, category: cat });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // Item 9 — LOCATION PREFILL FROM PROFILE. On a NEW listing, seed City/State/Zip from the
  // seller's profile so they don't retype where they are. Only fills EMPTY fields (never clobbers
  // a value the seller has already typed) and never runs while editing an existing listing.
  const [locPrefilled, setLocPrefilled] = useState(false);
  useEffect(() => {
    if (editing || !user) return;
    if (!(user.city || user.state || user.zip_code)) return;
    setForm((f) => {
      if (f.city || f.state || f.zip_code) return f;
      return { ...f, city: user.city || "", state: user.state || "", zip_code: user.zip_code || "" };
    });
    setLocPrefilled(true);
  }, [user, editing]);

  // DRAFTS — load a saved work-in-progress into the form when arriving at /create?draft=<id>.
  // A draft is the raw create-form payload from `db.listing_drafts` (private, never a listing),
  // so we just merge it over INIT. Runs once. Not applicable while editing a live listing.
  useEffect(() => {
    if (editing || draftLoadedRef.current) return;
    const did = new URLSearchParams(location.search).get("draft");
    if (!did) return;
    draftLoadedRef.current = true;
    api.get(`/listings/drafts/${did}`).then(({ data }) => {
      const p = data.payload || {};
      const clean = {};
      Object.keys(INIT).forEach((k) => { clean[k] = p[k] ?? INIT[k]; });
      setForm(clean);
      setDraftId(did);
    }).catch(() => { toast.error("That draft couldn't be found."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // MAP / anti-diversion + export control: NV/Thermal and IR-capable lasers can't be listed as
  // brand-NEW. If the seller had "New" selected and then picks a gated category/type, snap it back
  // to the default used tier so the dropdown never shows a value it no longer offers.
  useEffect(() => {
    const gated = form.category === NV_THERMAL_CAT || form.itar || (form.category === LASERS_CAT && laserIsIrCapable(form));
    if (gated && form.condition === "New") {
      setForm((f) => ({ ...f, condition: "Used - Like New" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category, form.condition, form.laser_type, form.laser_wavelength, form.itar]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    // The moment a seller edits a prefilled field, the catalog mark comes off it — it's theirs now, not ours.
    setPrefilled((p) => { if (!p.has(k)) return p; const n = new Set(p); n.delete(k); return n; });
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // One structured part-spec value (Barrels/Chassis/Uppers/Sights/Triggers).
  const setSpec = (key, val) => setForm((f) => ({ ...f, part_specs: { ...(f.part_specs || {}), [key]: val } }));
  const setKnifeSpec = (key, val) => setForm((f) => ({ ...f, knife_specs: { ...(f.knife_specs || {}), [key]: val } }));

  // VARIANTS — one sell listing, multiple purchasable options (reticle/tube/condition), each priced.
  const addVariant = () => setForm((f) => ({ ...f, variants: [...(f.variants || []), { label: "", price: "", quantity: 1, condition: "" }] }));
  const updVariant = (i, k, v) => setForm((f) => { const arr = [...(f.variants || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...f, variants: arr }; });
  const rmVariant = (i) => setForm((f) => ({ ...f, variants: (f.variants || []).filter((_, x) => x !== i) }));
  const hasVariants = form.listing_type === "sell" && (form.variants || []).length > 0;

  // Live share-card preview (debounced) — the exact card that renders when the listing link is
  // pasted. Built from the draft's first photo + title + price, before the listing is even saved.
  useEffect(() => {
    if (form.listing_type !== "sell") { setSharePreviewUrl(""); return; }
    const photo = (form.photos || [])[0];
    const title = (form.title || "").trim();
    if (!photo || !title) { setSharePreviewUrl(""); return; }
    const vPrices = (form.variants || []).map((v) => parseFloat(v.price)).filter((n) => n > 0);
    const price = hasVariants ? (vPrices.length ? Math.min(...vPrices) : "") : (parseFloat(form.price) || "");
    const t = setTimeout(() => {
      const q = new URLSearchParams({ title, price: price || "", photo, has_variants: hasVariants ? "1" : "0" });
      setSharePreviewUrl(`${API}/og/preview-image.jpg?${q.toString()}`);
    }, 600);
    return () => clearTimeout(t);
  }, [form.title, form.price, form.photos, form.variants, form.listing_type, hasVariants]);

  // Switching the shipping PATH clears the other path's fields so a stale value can't publish
  // (own-label -> buy-a-label drops the flat price and requires dimensions; and vice-versa).
  const setShipMode = (mode) => setForm((f) => ({
    ...f, ship_mode: mode,
    ...(mode === "buy_label"
      ? { flat_shipping: "", free_shipping: false }
      : { package_preset: "", pkg_length_in: "", pkg_width_in: "", pkg_height_in: "", pkg_weight_lb: "", pkg_weight_oz: "" }),
  }));

  // A shippable sell listing needs a package (buy-a-label) or a flat price (own-label).
  // BUY-A-LABEL is behind PLATFORM_LABELS_ENABLED (EasyPost pending) — with it OFF the ONLY path is
  // own-label, so we force the effective mode and never render the buy-a-label control.
  const labelsOn = platformLabelsEnabled;
  const shippable = form.listing_type === "sell" && form.shipping !== "pickup";
  const effectiveMode = labelsOn ? form.ship_mode : "own_label";
  const ownLabel = shippable && effectiveMode === "own_label";
  // Buyer-paid flat shipping (own-label, not free) — money the seller KEEPS; enters the commission basis.
  const buyerShipDollars = ownLabel && !form.free_shipping ? (parseFloat(form.flat_shipping) || 0) : 0;
  // Free-shipping postage the seller ABSORBS out of pocket — reduces true take-home, never the basis.
  const absorbDollars = ownLabel && form.free_shipping ? (parseFloat(form.flat_shipping) || 0) : 0;
  const absorbUnknown = ownLabel && form.free_shipping && !(form.flat_shipping !== "" && parseFloat(form.flat_shipping) >= 0);

  const scrollToPackage = () => {
    requestAnimationFrame(() => {
      packageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      packageRef.current?.querySelector("[data-package-focus]")?.focus?.();
    });
  };
  const setPackageError = (msg) => { setError(msg); setBusy(false); scrollToPackage(); };

  // Seller-facing DOLLAR fee preview from the server — the % rate is admin-only and never sent
  // to the client. Path-aware: own-label passes the flat shipping (money the seller keeps) so the
  // quoted payout is right for THIS listing. Debounced on price/type/shipping.
  useEffect(() => {
    const price = parseFloat(form.price);
    if (form.listing_type !== "sell" || !(price > 0)) { setFeePreview(null); return; }
    const t = setTimeout(() => {
      api.get(`/me/fee-quote?price=${price}&shipping=${buyerShipDollars}&absorb=${absorbDollars}`).then(({ data }) => setFeePreview(data)).catch(() => setFeePreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [form.price, form.listing_type, buyerShipDollars, absorbDollars]);

  // Path-aware "what you take home" quote for the onboarding card — never a hardcoded example.
  // Uses the live price (or a representative $1,425 until one is typed) and this listing's path.
  useEffect(() => {
    const price = parseFloat(form.price) > 0 ? parseFloat(form.price) : 1425;
    const t = setTimeout(() => {
      api.get(`/me/fee-quote?price=${price}&shipping=${buyerShipDollars}&absorb=${absorbDollars}`).then(({ data }) => setOnboardQuote(data)).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [form.price, buyerShipDollars, absorbDollars]);

  // ---- IDENTIFIER-FIRST catalog: resolve a UPC/SKU (or type-ahead) and prefill the spec fields ----
  // Maps catalog leaf-spec values onto the flat create form. `mk()` styles a prefilled field as the
  // distinct "third state" (olive left rail + tint); `CatalogMark` labels it. Both fall away on edit.
  const TUBE_FROM_CATALOG = { "1inch": "1 inch", '1"': "1 inch", "30mm": "30mm", "34mm": "34mm", "35mm": "35mm", "26mm": "Other" };
  const mk = (key) => `${inputCls}${prefilled.has(key) ? " border-l-2 border-l-olive-bright bg-olive/10" : ""}`;

  const applyCatalogPrefill = (res) => {
    const forSell = form.listing_type !== "buy";
    const specs = { ...(res.known || {}), ...(res.proposed || {}) };
    const next = {};
    const marked = new Set();
    const put = (k, v) => { if (v !== null && v !== undefined && String(v) !== "") { next[k] = String(v); marked.add(k); } };
    put("brand", res.brand);
    put("model", res.model);
    // Title + specs-block autofill (only when the seller hasn't typed their own yet — resolve is
    // step 1, so these are normally empty). Prefer the composed suggested_title (brand + model +
    // power×objective, e.g. "Pulsar Talion XQ35 2.5-10x35"); fall back to the raw source title.
    if (!form.title) put("title", res.suggested_title || res.source_title);
    if (res.description && !form.description) put("description", res.description);

    // PART CATEGORY (Barrels/Chassis/Uppers/Sights/Triggers/Handguards/Drones) — a part catalog row
    // carries the LEAF machine id (e.g. handguards.mlok). Set both the parent label (top select) and
    // the stored leaf, then map every catalog field onto the structured part_specs for that group.
    let nextPartSpecs = null;
    const partParentLabel = groupToParentLabel(res.leaf);  // bare group id (muzzle_devices) -> "Muzzle Devices"
    if (res.leaf && isPartCategory(res.leaf)) {
      // Specific leaf id (e.g. drones.consumer, handguards.mlok) — set parent label + stored leaf.
      const parent = parentLabelForLeaf(res.leaf);
      if (parent) { next.category_top = parent; marked.add("category"); }
      next.category = res.leaf;
      const group = partGroupOf(res.leaf);
      const cfg = PART_SPECS[group];
      if (cfg) {
        nextPartSpecs = {};
        cfg.fields.forEach((f) => {
          const raw = specs[f.key]?.value;
          if (raw === null || raw === undefined || String(raw) === "") return;
          if (f.type === "select") { if (f.options.includes(String(raw))) nextPartSpecs[f.key] = String(raw); }
          else if (f.type === "bool") { nextPartSpecs[f.key] = !!raw; }
          else nextPartSpecs[f.key] = String(raw);
        });
      }
    } else if (partParentLabel) {
      // Bare part-parent group id (muzzle_devices / ammunition / drones) — set the parent label and
      // auto-select the leaf when the parent has exactly one; otherwise the seller picks the sub-type.
      next.category_top = partParentLabel; marked.add("category");
      next.category = autoLeafForParent(partParentLabel);
    } else if (res.category_flat && isPartParent(res.category_flat)) {
      // category_flat itself is a part-parent label (leaf id absent) — same treatment.
      next.category_top = res.category_flat; marked.add("category");
      next.category = autoLeafForParent(res.category_flat);
    } else if (res.category_flat && CATEGORIES.includes(res.category_flat)) {
      put("category", res.category_flat); put("category_top", res.category_flat);
    } else if (res.category_flat && TOP_LEVEL_CATEGORIES.includes(res.category_flat)) {
      // Flat non-optic top-level category with no leaves (e.g. Bags & Packs).
      put("category", res.category_flat); put("category_top", res.category_flat);
    }
    // NV/Thermal share one flat category — the leaf tells the two halves apart, so set optic_type.
    if (res.leaf === "thermal") put("optic_type", "thermal");
    else if (res.leaf === "night_vision") put("optic_type", "night_vision");
    // Night Vision & Thermal structured fields — form factor / sensor / detection range.
    const dev = specs.device_type?.value;
    if (dev && DEVICE_TYPES.includes(String(dev))) put("device_type", String(dev));
    const sensor = specs.sensor_resolution?.value;
    if (sensor) put("sensor_resolution", String(sensor));
    const detect = specs.detection_range?.value;
    if (detect != null && String(detect) !== "" && Number.isFinite(Number(detect))) put("detection_range", Number(detect));
    const magVal = specs.magnification?.value;
    if (magVal) {
      put("magnification", magVal);
      const m = String(magVal).match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
      if (m) { put("mag_min", m[1]); put("mag_max", m[2]); }
    }
    const tube = specs.tube_diameter?.value;
    if (tube) { const mapped = TUBE_FROM_CATALOG[String(tube).toLowerCase().replace(/\s/g, "")]; if (mapped) put("tube_diameter", mapped); }
    const fp = specs.focal_plane?.value;
    if (fp && ["FFP", "SFP", "N/A"].includes(String(fp).toUpperCase())) put("focal_plane", String(fp).toUpperCase());
    if (forSell) {  // weight, dims + shipping are never pulled onto a Want-to-Buy
      const n = Number(specs.weight_lb?.value);
      if (Number.isFinite(n) && n > 0) { const lb = Math.floor(n); const oz = +(((n % 1) * 16).toFixed(1)); if (lb) put("wt_lb", lb); if (oz) put("wt_oz", oz); }
    }
    // Sight/red-dot spec fields (reticle / mount / color) — only set when the catalog value maps
    // to a real select option, so we never write an unselectable value.
    const reticle = specs.reticle?.value;
    if (reticle && RETICLES.includes(String(reticle))) put("reticle_type", String(reticle));
    const mount = specs.mount?.value;
    if (mount && MOUNTS.includes(String(mount))) put("mount_type", String(mount));
    const color = specs.color?.value;
    if (color && COLORS.includes(String(color))) put("color", String(color));
    setForm((f) => ({ ...f, ...next, ...(nextPartSpecs ? { part_specs: { ...(f.part_specs || {}), ...nextPartSpecs } } : {}) }));
    setPrefilled(marked);
  };

  const onCatalogResolve = (res) => {
    if (!res || res.result === "unresolved") {
      setCatalog(null); setCatalogBlock(null); setSpecsCollapsed(false);
      setCatalogMiss(res?.identifier || "that code");  // surface "not found — fill in manually"
      if (res?.identifier) {  // log the miss so admins can see what to add next
        api.post("/catalog/candidate", { identifier: res.identifier, identifier_type: "upc", typed: { brand: form.brand, model: form.model } }).catch(() => {});
      }
      return;
    }
    if (res.result === "blocked") {  // refuse by name — a blocked product can't be posted (sell OR wtb)
      setCatalog(null); setCatalogMiss(null); setSpecsCollapsed(false);
      setCatalogBlock({ message: res.block_message, brand: res.brand, model: res.model });
      return;
    }
    setCatalogBlock(null);
    setCatalogMiss(null);
    setCatalog(res);
    applyCatalogPrefill(res);
    setSpecsCollapsed(true);
  };

  const clearCatalog = () => { setCatalog(null); setCatalogBlock(null); setCatalogMiss(null); };

  // Condition set for the current category. NV/Thermal drops "New" (MAP); an IR-capable laser
  // drops "New" too (export / anti-diversion). Visible-only lasers keep every condition.
  const laserIrGated = form.category === LASERS_CAT && laserIsIrCapable(form);
  const noNewCondition = form.category === NV_THERMAL_CAT || laserIrGated || form.itar;
  const conditionOptions = noNewCondition ? CONDITIONS.filter((c) => c !== "New") : CONDITIONS;

  // SAVE DRAFT — explicit, private work-in-progress. Stores the raw form in `db.listing_drafts`
  // (its own collection; never a listing, never in the feed/search/counts). No validation gate:
  // ITAR/attestation/condition rules only run at Post. Re-saving updates the same draft.
  const saveDraft = async () => {
    if (editing) return;
    setSavingDraft(true);
    try {
      const { data } = await api.post("/listings/drafts", { draft_id: draftId, payload: form });
      const newId = data.draft?.draft_id;
      if (newId && !draftId) {
        setDraftId(newId);
        draftLoadedRef.current = true; // we own this draft now; don't re-load it
        navigate(`/create?draft=${newId}`, { replace: true });
      }
      toast.success("Draft saved — find it on your dashboard.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSavingDraft(false);
    }
  };

  const summaryChips = [
    { k: "category", label: "Category", value: form.category },
    { k: "mag", label: "Mag", value: form.magnification || (form.mag_min && form.mag_max ? `${form.mag_min}-${form.mag_max}x` : "") },
    { k: "tube", label: "Tube", value: form.tube_diameter },
    { k: "fp", label: "Focal plane", value: form.focal_plane },
  ].filter((c) => c.value);

  const submit = async (e) => {
    e.preventDefault();
    if (emailUnverified) { toast.error("Verify your email before publishing a listing."); return; }
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (catalogBlock) { setError(`${catalogBlock.message} This item can't be posted on OpticSwap.`); return; }
    if (isPartParent(form.category_top) && !form.category) {
      setError(`Choose a specific ${form.category_top} type from the second menu.`); return;
    }
    if (isPartCategory(form.category)) {
      const proh = detectProhibited(form.title, form.description, form.brand, form.model);
      if (proh && proh.block) { setError(proh.block); return; }
      if (partGroupOf(form.category) === "uppers" && !editing && !form.uppers_attest) {
        setError("Please confirm the upper-configuration attestation before publishing."); return;
      }
    }
    if (!editing && !form.attestation) { setError("Please confirm the seller attestation before publishing."); return; }
    // PACKAGE / SHIPPING — required to publish a shippable sell listing (the original blocker).
    if (shippable) {
      if (effectiveMode === "own_label") {
        if (!form.free_shipping && !(form.flat_shipping !== "" && parseFloat(form.flat_shipping) >= 0)) {
          setPackageError("Set a flat shipping cost, or turn on Free shipping, before publishing.");
          return;
        }
      } else {
        const preset = (form.package_preset || "").toLowerCase();
        const hasPreset = preset && preset !== "custom";
        const dimsOk = [form.pkg_length_in, form.pkg_width_in, form.pkg_height_in].every((d) => parseFloat(d) > 0);
        const wtOk = (parseFloat(form.pkg_weight_lb) || 0) + (parseFloat(form.pkg_weight_oz) || 0) > 0;
        if (!hasPreset && !(dimsOk && wtOk)) {
          setPackageError("Add the package size — pick a preset, or enter length, width, height and weight.");
          return;
        }
      }
    }
    setBusy(true);
    setError("");
    const numOrNull = (v) => (v === "" || v == null ? null : parseFloat(v));
    const specWtOz = (parseFloat(form.wt_lb) || 0) * 16 + (parseFloat(form.wt_oz) || 0);
    const pkgWeightLb = (parseFloat(form.pkg_weight_lb) || 0) + (parseFloat(form.pkg_weight_oz) || 0) / 16;
    const buyLabel = shippable && effectiveMode === "buy_label";
    // Part specs: send only the fields VISIBLE for the selected leaf (so e.g. a Grain Weight typed
    // before switching to Shot Shell, or a hidden AR-only field, never rides along stale).
    const _pg = partGroupOf(form.category);
    const partSpecsOut = _pg
      ? (() => {
          const vis = new Set(visibleFieldsFor(_pg, form.part_specs, form.category).map((f) => f.key));
          return Object.fromEntries(Object.entries(form.part_specs || {}).filter(([k]) => vis.has(k)));
        })()
      : {};
    const payload = {
      ...form,
      part_specs: partSpecsOut,
      price: parseFloat(form.price) || 0,
      mag_min: numOrNull(form.mag_min),
      mag_max: numOrNull(form.mag_max),
      weight_oz: specWtOz > 0 ? +specWtOz.toFixed(2) : null,
      // Shipping path
      ship_mode: shippable ? effectiveMode : null,
      flat_shipping: (shippable && effectiveMode === "own_label" && !form.free_shipping) ? (parseFloat(form.flat_shipping) || 0) : null,
      free_shipping: shippable && effectiveMode === "own_label" ? !!form.free_shipping : false,
      // Package (buy-a-label). A preset fills the numbers server-side; custom sends them.
      package_preset: buyLabel ? (form.package_preset || "custom") : null,
      package_length_in: buyLabel ? numOrNull(form.pkg_length_in) : null,
      package_width_in: buyLabel ? numOrNull(form.pkg_width_in) : null,
      package_height_in: buyLabel ? numOrNull(form.pkg_height_in) : null,
      package_weight_lb: buyLabel ? (pkgWeightLb > 0 ? +pkgWeightLb.toFixed(3) : null) : null,
      // Variants — sell only; drop blank rows, coerce numbers, preserve vid on edit.
      variants: form.listing_type === "sell"
        ? (form.variants || [])
            .filter((v) => String(v.label || "").trim() && v.price !== "" && v.price != null)
            .map((v) => ({ ...(v.vid ? { vid: v.vid } : {}), label: String(v.label).trim(), price: parseFloat(v.price) || 0, quantity: parseInt(v.quantity, 10) || 1, condition: v.condition || "" }))
        : [],
      // Best Offer — coerce blank threshold strings to null; cleared unless offers are on.
      accept_offers: form.listing_type === "sell" ? !!form.accept_offers : false,
      offer_min: form.listing_type === "sell" && form.accept_offers ? numOrNull(form.offer_min) : null,
      offer_auto_accept: form.listing_type === "sell" && form.accept_offers ? numOrNull(form.offer_auto_accept) : null,
      // Knife specs only ride along for the Knives & Blades category (never stale on other categories).
      knife_specs: form.category === KNIVES_CATEGORY ? (form.knife_specs || {}) : {},
      // NV/Thermal structured fields — only meaningful for that category; cleared otherwise.
      device_type: form.category === NV_THERMAL_CAT ? (form.device_type || "") : "",
      sensor_resolution: form.category === NV_THERMAL_CAT ? (form.sensor_resolution || "") : "",
      detection_range: form.category === NV_THERMAL_CAT ? numOrNull(form.detection_range) : null,
      // Provenance (optional, sell listings). Send only the active age field; warranty is free text.
      purchase_date: form.listing_type !== "buy" && form.age_mode === "date" ? (form.purchase_date || "") : "",
      approx_age: form.listing_type !== "buy" && form.age_mode === "approx" ? String(form.approx_age || "").trim() : "",
      warranty_remaining: form.listing_type !== "buy" ? String(form.warranty_remaining || "").trim() : "",
    };
    try {
      if (editing) {
        await api.put(`/listings/${id}`, payload);
        toast.success("Listing updated.");
        navigate(`/listing/${id}`);
      } else {
        const { data } = await api.post("/listings", payload);
        if (draftId) api.delete(`/listings/drafts/${draftId}`).catch(() => {}); // published — clear the draft
        if (data.pending_review) {
          toast.success("Submitted for review — your first listing goes live once our team approves it. You'll be notified.");
          navigate("/dashboard");
        } else if (data.draft) {
          toast.success("Saved as a draft. It publishes automatically once your seller payouts are set up.");
          navigate("/dashboard");
        } else if (data.charged > 0) {
          if (data.listings_milestone) celebrateMilestone(data.listings_milestone);
          toast.success(`Listing live! $${data.charged.toFixed(2)} post fee applied.`);
          navigate(listingPath(data.listing));
        } else {
          if (data.listings_milestone) celebrateMilestone(data.listings_milestone);
          else toast.success("Listing is live!");
          navigate(listingPath(data.listing));
        }
      }
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || err.message;
      setError(msg);
      if (/package|shipping|dimension/i.test(msg || "")) scrollToPackage();
    } finally {
      setBusy(false);
    }
  };

  const isPart = isPartCategory(form.category);
  const isKnife = form.category === KNIVES_CATEGORY;
  const partGroup = isPart ? partGroupOf(form.category) : null;
  const isParentSel = isPartParent(form.category_top);          // a part parent chosen at top level
  const partLeaves = isParentSel ? leavesForParentLabel(form.category_top) : [];
  const needsLeaf = isParentSel && !form.category;             // parent picked, leaf not yet chosen
  const F = isPart ? {} : specFields(form.category);
  // Serialized-firearm guard, evaluated LIVE as the seller types — shown BEFORE the Post button so
  // no one writes a whole listing only to be refused on submit. Only for the new part categories.
  const prohibited = isFirearmPartCategory(form.category) ? detectProhibited(form.title, form.description, form.brand, form.model) : null;
  const prohibitedBlock = prohibited && prohibited.block ? prohibited.block : null;
  const prohibitedReview = prohibited && prohibited.review ? prohibited.review : null;
  const needsUppersAttest = partGroup === "uppers";

  // Top-level category change: an optic sets the stored category directly; a part PARENT clears the
  // stored category until the seller picks a specific leaf type in the second selection.
  const onTopCategory = (e) => {
    const v = e.target.value;
    setPrefilled((p) => { if (!p.has("category")) return p; const n = new Set(p); n.delete("category"); return n; });
    if (isPartParent(v)) {
      setForm((f) => ({ ...f, category_top: v, category: autoLeafForParent(v), part_specs: {}, uppers_attest: false }));
    } else {
      setForm((f) => ({ ...f, category_top: v, category: v }));
    }
  };
  const pickLeaf = (v) => setForm((f) => ({ ...f, category: v }));
  const onLeaf = (e) => pickLeaf(e.target.value);

  // PLAN GATE: the base un-subscribed account is browse/buy/message only. Selling requires an
  // active plan. Show an explainer + route to /pricing — never a form/GO LIVE that can only 403.
  const needsPlan = !editing && !!quota && quota.needs_plan === true;
  if (needsPlan) {
    const planCards = (tiers || []).filter((t) => t.active && !t.hidden);
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-8">
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="create-title">Post A Listing</h1>
          <Crosshair className="my-6" />
          <div className="border border-dashed border-sniper bg-surface p-8" data-testid="create-plan-gate">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 border border-sniper rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-olive-bright" />
              </div>
              <h2 className="font-heading text-2xl uppercase tracking-tight mt-4">A plan is required to sell</h2>
              <p className="text-sm font-body text-secondary-foreground mt-3 max-w-md mx-auto leading-relaxed">
                Browsing, buying, messaging, and ID verification are always free. To list gear for sale,
                choose a membership below — it's what keeps the marketplace serious.
              </p>
            </div>
            {planCards.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4 mt-7" data-testid="plan-gate-cards">
                {planCards.map((t) => (
                  <div key={t.id} className="border border-sniper bg-obsidian/60 p-5" data-testid={`plan-gate-card-${t.id}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-heading uppercase text-lg tracking-tight">{t.label}</span>
                      <span className="font-mono text-olive-bright text-sm">${(t.monthly_price_cents / 100).toFixed(0)}/mo</span>
                    </div>
                    <ul className="mt-3 space-y-1.5 font-body text-[13px] text-secondary-foreground">
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-olive mt-0.5 shrink-0" /> {t.free_listings} free listing{t.free_listings === 1 ? "" : "s"} / month</li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-olive mt-0.5 shrink-0" /> {t.commission_pct}% platform fee on sales</li>
                      {t.free_priority_listings > 0 && (
                        <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-olive mt-0.5 shrink-0" /> {t.free_priority_listings} included bumps / month</li>
                      )}
                      {t.dedicated_support && (
                        <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-olive mt-0.5 shrink-0" /> Priority seller support</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <div className="text-center mt-7">
              <button onClick={() => navigate("/pricing")} data-testid="create-plan-gate-pricing-btn"
                className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-6 py-3 transition-colors">
                View plans & subscribe <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-4 text-xs font-body text-muted-foreground">
                Already have live listings? They stay up — you can still edit, mark them sold, and ship them.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (idGate) {
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-8">
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="create-title">Post A Listing</h1>
          <Crosshair className="my-6" />
          <div className="border border-dashed border-sniper bg-surface p-8 text-center" data-testid="create-identity-gate">
            <div className="mx-auto w-12 h-12 border border-sniper rounded-full flex items-center justify-center">
              <Lock className="w-6 h-6 text-olive-bright" />
            </div>
            <h2 className="font-heading text-2xl uppercase tracking-tight mt-4">Verification required to list</h2>
            <p className="text-sm font-body text-secondary-foreground mt-3 max-w-md mx-auto leading-relaxed">
              Sellers verify their identity before posting. It's a two-minute check and it's what makes buyers
              here trust a stranger's listing.
            </p>
            <button onClick={() => setVerifyOpen(true)} data-testid="create-gate-verify-btn"
              className="mt-6 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-6 py-3 transition-colors">
              <ShieldCheck className="w-4 h-4" /> Verify my ID <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <VerifyIdFlow open={verifyOpen} onClose={() => setVerifyOpen(false)} next="/create" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8">
        {returnTo && (
          <button
            type="button"
            onClick={() => navigate(returnTo)}
            data-testid="create-back-to-admin"
            className="inline-flex items-center gap-1.5 mb-4 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-1.5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} /> Back
          </button>
        )}
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="create-title">
          {editing ? "Edit Listing" : "Post A Listing"}
        </h1>
        <p className="text-sm text-secondary-foreground font-body mt-1">Structured fields power real search. Fill what applies.</p>

        {!editing && (
          <div className="mt-5">
            <OnboardingSteps
              user={user}
              payout={payoutStatus}
              quote={onboardQuote}
              resending={resending}
              onResendEmail={resendVerification}
              onVerifyId={() => setVerifyOpen(true)}
            />
          </div>
        )}

        {quota && !editing && !quota.unlimited && (
          <div className={`mt-4 border px-4 py-3 font-mono text-xs uppercase tracking-wide ${quota.next_is_paid ? "border-blaze/50 bg-blaze/10 text-blaze" : "border-olive/40 bg-olive/10 text-olive-bright"}`} data-testid="quota-banner">
            {quota.next_is_paid
              ? `Free quota used (${quota.used}/${quota.free_limit}). This post costs $${quota.post_fee.toFixed(2)}.`
              : `${quota.remaining} of ${quota.free_limit} free listings remaining this month.`}
          </div>
        )}

        <Crosshair className="my-6" />

        {!editing && (
          <p className="mb-5 text-[11px] font-body text-muted-foreground border-l-2 border-sniper pl-3" data-testid="seller-commitment-notice">
            By publishing, you agree to complete this sale on OpticSwap while the listing is active. Selling the item off-platform is a violation of the seller terms.
          </p>
        )}

        <form onSubmit={submit} className="space-y-5">
          {error && <div data-testid="create-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{error}</div>}

          {/* Category first: it drives which structured spec fields appear further down the form,
              so it's the seller's first decision — before photos, before anything else. */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Listing Type</label>
              <select value={form.listing_type} onChange={set("listing_type")} className={inputCls} data-testid="create-listing-type">
                {LISTING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Category{prefilled.has("category") && <CatalogMark k="category" />}</label>
              <select value={form.category_top} onChange={onTopCategory} className={mk("category")} data-testid="create-category">
                {TOP_LEVEL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {isParentSel && partLeaves.length > 1 && (
                <div className="mt-2">
                  <label className={labelCls}>{form.category_top} type</label>
                  {form.category_top === "Muzzle Devices" ? (
                    <div className="flex flex-wrap gap-2" data-testid="muzzle-type-pills">
                      {partLeaves.map((l) => {
                        const active = form.category === l.id;
                        return (
                          <button
                            key={l.id}
                            type="button"
                            onClick={() => pickLeaf(l.id)}
                            aria-pressed={active}
                            data-testid={`muzzle-type-${l.id.split(".")[1]}`}
                            className={`px-3 py-1.5 border font-mono text-[11px] uppercase tracking-wide transition-colors ${active ? "border-olive bg-olive/15 text-foreground" : "border-sniper text-secondary-foreground hover:border-olive/50"}`}
                          >
                            {l.short}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <select value={form.category} onChange={onLeaf} className={inputCls} data-testid="create-part-leaf">
                      <option value="">Pick a {form.category_top.toLowerCase()} type…</option>
                      {partLeaves.map((l) => <option key={l.id} value={l.id}>{l.short}</option>)}
                    </select>
                  )}
                </div>
              )}
            </div>
          </div>

          {!editing && (
            <CatalogIdentifier
              onResolve={onCatalogResolve}
              onNoId={() => {}}
              onClear={clearCatalog}
              showNoId={form.listing_type !== "buy"}
              wtb={form.listing_type === "buy"}
              resolvedSummary={catalog ? `Prefilled from catalog — ${[catalog.brand, catalog.model].filter(Boolean).join(" ")}. Review and edit below.` : ""}
              blockedInfo={catalogBlock ? catalogBlock.message : ""}
              notFoundInfo={catalogMiss ? `No match for ${catalogMiss} in our catalog yet — no problem, just fill in the details below by hand. We've logged it to add soon.` : ""}
            />
          )}

          {/* photos */}
          {/* photos — hidden for Want-to-Buy: the poster doesn't own the item, so there's nothing
              to photograph (reference photos are a later phase). */}
          {form.listing_type !== "buy" && (
          <div>
            <label className={labelCls}>Photos (up to 10) — drag to reorder, first is the cover</label>
            <ListingPhotoUploader
              value={form.photos}
              onChange={(photos) => setForm((f) => ({ ...f, photos }))}
            />
            <p className="mt-2 text-[11px] font-mono text-muted-foreground" data-testid="photo-upload-help">JPG, PNG, WebP, HEIC · up to 10 MB each · 10 photos max · drag or paste to add, drag thumbnails to reorder</p>
          </div>
          )}

          <div>
            <label className={labelCls}>Video URL (optional)</label>
            <input value={form.video} onChange={set("video")} placeholder="https://..." className={inputCls} data-testid="create-video" />
          </div>

          <div>
            <label className={labelCls}>Title</label>
            <input required value={form.title} onChange={set("title")} placeholder="e.g. Vortex Razor HD Gen III 6-36x56 FFP" className={inputCls} data-testid="create-title-input" />
          </div>

          {/* Serialized-firearm guard — shown LIVE, before Post, so nobody writes a whole listing
              only to be refused on submit. Hard block (barreled action) vs review notice (lower/serial). */}
          {prohibitedBlock && (
            <div className="border border-blaze bg-blaze/10 text-blaze px-4 py-3 flex items-start gap-2" data-testid="create-prohibited-block">
              <Ban className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="text-sm font-body">
                <span className="font-heading uppercase tracking-tight block mb-0.5">Can't be listed</span>
                {prohibitedBlock} You won't be able to post this — remove the item or choose a legal, non-serialized part.
              </div>
            </div>
          )}
          {!prohibitedBlock && prohibitedReview && (
            <div className="border border-[#C2600F]/50 bg-[#C2600F]/10 text-[#E8C46A] px-4 py-3 flex items-start gap-2" data-testid="create-prohibited-review">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="text-sm font-body">{prohibitedReview} You can still post — it just won't go live until a moderator clears it.</div>
            </div>
          )}

          {catalog && specsCollapsed ? (
          <div className="border border-olive/50 bg-olive/[0.06] p-4" data-testid="catalog-summary-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-olive-bright" strokeWidth={1.8} />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-olive-bright">Prefilled from catalog</span>
                </div>
                <p className="font-heading uppercase text-base tracking-tight text-foreground" data-testid="catalog-summary-title">{[catalog.brand, catalog.model].filter(Boolean).join(" ") || "Matched product"}</p>
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="catalog-summary-chips">
                  {summaryChips.map((c) => (
                    <span key={c.k} className="font-mono text-[10px] uppercase px-2 py-1 border border-olive/40 text-secondary-foreground">{c.label}: <span className="text-foreground">{c.value}</span></span>
                  ))}
                </div>
                <p className="text-[11px] font-body text-muted-foreground mt-2">Everything is editable — the moment you change a field it becomes yours, not the catalog's.</p>
              </div>
              <button type="button" onClick={() => setSpecsCollapsed(false)} data-testid="catalog-summary-edit"
                className="shrink-0 border border-olive hover:bg-olive/10 text-olive-bright font-mono uppercase text-[11px] px-3 py-2 transition-colors">Edit details</button>
            </div>
          </div>
          ) : (
          <div className="border border-sniper bg-surface p-4 space-y-4" data-testid="create-identification">
          <div className="flex items-center justify-between gap-2">
            <label className={labelCls} style={{ marginBottom: 0 }}>Identification &amp; specs</label>
            {catalog && (
              <button type="button" onClick={() => setSpecsCollapsed(true)} data-testid="catalog-summary-collapse"
                className="border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2.5 py-1 transition-colors">Show summary</button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{partGroup === "ammunition" ? "Manufacturer" : "Brand"}{prefilled.has("brand") && <CatalogMark k="brand" />}</label>
              <input value={form.brand} onChange={set("brand")} placeholder={partGroup === "ammunition" ? "Federal" : "Vortex"} list="brand-list" autoComplete="off" className={mk("brand")} data-testid="create-brand" />
              <datalist id="brand-list" data-testid="brand-datalist">
                {(partGroup === "ammunition" ? AMMO_MANUFACTURERS : brandList).map((b) => <option key={b} value={b} />)}
              </datalist>
            </div>
            <div>
              <label className={labelCls}>Model (optional){prefilled.has("model") && <CatalogMark k="model" />}</label>
              <input value={form.model} onChange={set("model")} placeholder="Razor HD Gen III" className={mk("model")} data-testid="create-model" />
            </div>
          </div>

          {Object.keys(F).length > 0 && (
            <div className="space-y-4">
              {F.opticType && (
                <div>
                  <label className={labelCls}>Type</label>
                  <select value={form.optic_type} onChange={set("optic_type")} className={inputCls} data-testid="create-optic-type">
                    <option value="">—</option>
                    <option value="thermal">Thermal</option>
                    <option value="night_vision">Night vision</option>
                  </select>
                </div>
              )}
              {F.thermal && (
                <div className="grid sm:grid-cols-3 gap-4" data-testid="create-thermal-specs">
                  <div>
                    <label className={labelCls}>Device type{prefilled.has("device_type") && <CatalogMark k="device_type" />}</label>
                    <select value={form.device_type} onChange={set("device_type")} className={mk("device_type")} data-testid="create-device-type">
                      <option value="">—</option>
                      {DEVICE_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Sensor resolution{prefilled.has("sensor_resolution") && <CatalogMark k="sensor_resolution" />}</label>
                    <input list="sensor-res-list" value={form.sensor_resolution} onChange={set("sensor_resolution")} placeholder="640x512" className={mk("sensor_resolution")} data-testid="create-sensor-resolution" />
                    <datalist id="sensor-res-list">{SENSOR_RESOLUTIONS.map((s) => <option key={s} value={s} />)}</datalist>
                  </div>
                  <div>
                    <label className={labelCls}>Detection range (yd){prefilled.has("detection_range") && <CatalogMark k="detection_range" />}</label>
                    <input type="number" min="0" step="1" value={form.detection_range} onChange={set("detection_range")} placeholder="e.g. 2700" className={mk("detection_range")} data-testid="create-detection-range" />
                  </div>
                </div>
              )}
              {F.laser && (
                <div className="space-y-4" data-testid="create-laser-specs">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Type</label>
                      <select value={form.laser_type} onChange={set("laser_type")} className={inputCls} data-testid="create-laser-type">
                        <option value="">—</option>
                        {LASER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Wavelength / Class</label>
                      <select value={form.laser_wavelength} onChange={set("laser_wavelength")} className={inputCls} data-testid="create-laser-wavelength">
                        <option value="">—</option>
                        {LASER_WAVELENGTHS.map((w) => <option key={w} value={w}>{w}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Battery</label>
                      <select value={form.laser_battery} onChange={set("laser_battery")} className={inputCls} data-testid="create-laser-battery">
                        <option value="">—</option>
                        {LASER_BATTERIES.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer self-end pb-2.5" data-testid="create-laser-adjustable-row">
                      <input type="checkbox" checked={form.laser_adjustable} onChange={set("laser_adjustable")} className="accent-olive w-4 h-4" data-testid="create-laser-adjustable" />
                      <span className="font-mono text-xs uppercase text-secondary-foreground">Windage &amp; elevation adjustable</span>
                    </label>
                  </div>
                  {laserIrGated && (
                    <div className="border border-[#C2600F]/50 bg-[#C2600F]/10 text-[#E8C46A] px-4 py-3 flex items-start gap-2" data-testid="laser-export-note">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <div className="text-sm font-body">
                        IR-capable lasers &amp; illuminators are export-controlled (ITAR). You can still post — it won&apos;t go live until a moderator clears it, and it can only be listed as <span className="font-semibold">used</span> (not New).
                      </div>
                    </div>
                  )}
                  {laserIrGated && (
                    <div className="border border-sniper bg-surface px-4 py-3" data-testid="laser-photo-tips">
                      <div className="flex items-center gap-2 mb-2">
                        <Camera className="w-4 h-4 text-olive-bright shrink-0" />
                        <span className="font-mono text-[11px] uppercase tracking-wide text-olive-bright">Photographing an IR laser safely</span>
                      </div>
                      <ul className="text-[13px] font-body text-secondary-foreground leading-relaxed list-disc pl-5 space-y-1">
                        <li><span className="text-foreground">Never fire the laser toward a camera, eyes, or anyone</span> — IR is invisible but can damage vision and sensors.</li>
                        <li>Keep the unit <span className="text-foreground">powered off</span> for photos. Don&apos;t try to capture the beam.</li>
                        <li>Shoot in good light against a plain background; show the body, controls, and <span className="text-foreground">serial number / model markings</span> clearly.</li>
                        <li>Include the <span className="text-foreground">battery compartment and mount</span>, plus any wear, so buyers see true condition.</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <div className="grid sm:grid-cols-4 gap-4">
                {F.magMinMax && (
                  <>
                    <div>
                      <label className={labelCls}>Min Mag (×){prefilled.has("mag_min") && <CatalogMark k="mag_min" />}</label>
                      <input type="number" min="0" step="0.1" value={form.mag_min} onChange={set("mag_min")} className={mk("mag_min")} data-testid="create-mag-min" />
                    </div>
                    <div>
                      <label className={labelCls}>Max Mag (×){prefilled.has("mag_max") && <CatalogMark k="mag_max" />}</label>
                      <input type="number" min="0" step="0.1" value={form.mag_max} onChange={set("mag_max")} className={mk("mag_max")} data-testid="create-mag-max" />
                    </div>
                  </>
                )}
                {F.magRange && (
                  <div>
                    <label className={labelCls}>Mag Range</label>
                    <select value={form.mag_range} onChange={set("mag_range")} className={inputCls} data-testid="create-mag-range">
                      <option value="">—</option>
                      {MAG_RANGES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                {F.reticle && (
                  <div>
                    <label className={labelCls}>Reticle{prefilled.has("reticle_type") && <CatalogMark k="reticle_type" />}</label>
                    <select value={form.reticle_type} onChange={set("reticle_type")} className={mk("reticle_type")} data-testid="create-reticle">
                      <option value="">—</option>
                      {RETICLES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-4 gap-4">
                {F.tube && (
                  <div>
                    <label className={labelCls}>Tube Diameter{prefilled.has("tube_diameter") && <CatalogMark k="tube_diameter" />}</label>
                    <select value={form.tube_diameter} onChange={set("tube_diameter")} className={mk("tube_diameter")} data-testid="create-tube">
                      <option value="">—</option>
                      {TUBE_DIAMETERS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                {F.focal && (
                  <div>
                    <label className={labelCls}>Focal Plane{prefilled.has("focal_plane") && <CatalogMark k="focal_plane" />}</label>
                    <select value={form.focal_plane} onChange={set("focal_plane")} className={mk("focal_plane")} data-testid="create-focal-plane">
                      <option value="">—</option>
                      {FOCAL_PLANES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                {F.weight && form.listing_type !== "buy" && (
                  <>
                    <div>
                      <label className={labelCls}>Weight (lb){prefilled.has("wt_lb") && <CatalogMark k="wt_lb" />}</label>
                      <input type="number" min="0" step="1" value={form.wt_lb} onChange={set("wt_lb")} className={mk("wt_lb")} data-testid="create-weight-lb" />
                    </div>
                    <div>
                      <label className={labelCls}>Weight (oz){prefilled.has("wt_oz") && <CatalogMark k="wt_oz" />}</label>
                      <input type="number" min="0" max="15.9" step="0.1" value={form.wt_oz} onChange={set("wt_oz")} className={mk("wt_oz")} data-testid="create-weight-oz" />
                    </div>
                  </>
                )}
                {F.mount && (
                  <div>
                    <label className={labelCls}>Mount / Interface{prefilled.has("mount_type") && <CatalogMark k="mount_type" />}</label>
                    <select value={form.mount_type} onChange={set("mount_type")} className={mk("mount_type")} data-testid="create-mount">
                      <option value="">—</option>
                      {MOUNTS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
              </div>
              {F.magLabel && (
                <div>
                  <label className={labelCls}>Mag label (optional, as printed — e.g. 6-36×56){prefilled.has("magnification") && <CatalogMark k="magnification" />}</label>
                  <input value={form.magnification} onChange={set("magnification")} placeholder="6-36x56" className={mk("magnification")} data-testid="create-magnification" />
                </div>
              )}
            </div>
          )}
          {isPart && partGroup && (
            <div className="pt-4 border-t border-sniper">
              <PartSpecs group={partGroup} specs={form.part_specs} setSpec={setSpec} leaf={form.category} />
              {partGroup === "ammunition" && (
                <div className="mt-4 flex items-start gap-3 border border-[#d3a24e]/40 bg-[#d3a24e]/5 p-4" data-testid="ammo-shipping-note">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[#d3a24e]" />
                  <span className="font-body text-[13px] text-secondary-foreground leading-relaxed">
                    <b className="text-foreground">Shipping ammunition:</b> ammo ships <b className="text-foreground">UPS or FedEx Ground only</b> — USPS won't carry it, so it's excluded from the buyer's rate options. When you buy your platform label, a <b className="text-foreground">DOT Limited Quantity</b> hazmat marking prints with it (page 2) to affix to the box.
                  </span>
                </div>
              )}
              {needsUppersAttest && !editing && (
                <label className="mt-4 flex items-start gap-3 border border-blaze/40 bg-blaze/5 p-4 cursor-pointer" data-testid="uppers-attestation">
                  <input type="checkbox" checked={form.uppers_attest} onChange={set("uppers_attest")} className="accent-blaze w-4 h-4 mt-0.5 shrink-0" data-testid="uppers-attestation-checkbox" />
                  <span className="font-body text-[13px] text-secondary-foreground leading-relaxed">{PART_SPECS.uppers.attestation}</span>
                </label>
              )}
            </div>
          )}
          {isKnife && (
            <div className="pt-4 border-t border-sniper">
              <KnifeSpecs specs={form.knife_specs} setSpec={setKnifeSpec} />
            </div>
          )}
          </div>
          )}

          {form.listing_type === "buy" ? (
            <div className="space-y-4" data-testid="wtb-fields">
              <p className="text-[11px] font-mono text-muted-foreground border border-sniper bg-obsidian/40 px-3 py-2" data-testid="wtb-intro">
                An ISO ("In Search Of") post — you're looking to <span className="text-olive-bright">buy or trade for</span> this item. Sellers who have one can reach out.
              </p>
              <div>
                <label className={labelCls}>Condition I'll accept (select all that apply)</label>
                <div className="flex flex-wrap gap-2" data-testid="wtb-conditions">
                  {CONDITIONS.map((c) => {
                    const on = (form.wtb_conditions || []).includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        data-testid={`wtb-condition-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setForm((f) => {
                          const cur = f.wtb_conditions || [];
                          return { ...f, wtb_conditions: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] };
                        })}
                        className={`font-mono text-xs uppercase px-3 py-2 border transition-colors ${on ? "border-olive-bright bg-olive/10 text-foreground" : "border-sniper text-secondary-foreground hover:border-olive/50"}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Budget / max price ($)</label>
                  <input type="number" min="0" step="0.01" value={form.price} onChange={set("price")} placeholder="e.g. 850" className={inputCls} data-testid="create-price" />
                  <p className="mt-1 text-[11px] font-mono text-muted-foreground">The most you'll pay. Sellers see this as your ceiling.</p>
                </div>
                <div>
                  <label className={labelCls}>How soon</label>
                  <select value={form.wtb_timeframe} onChange={set("wtb_timeframe")} className={inputCls} data-testid="wtb-timeframe">
                    <option value="">—</option>
                    {WTB_TIMEFRAMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ) : (
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Condition</label>
              <select value={form.condition} onChange={set("condition")} className={inputCls} data-testid="create-condition">
                {conditionOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {form.category === NV_THERMAL_CAT && (
                <p className="mt-1 text-[11px] font-mono text-muted-foreground" data-testid="nv-condition-note">
                  New-condition NV/Thermal isn't allowed (MAP / anti-diversion) — list it as used.
                </p>
              )}
              {laserIrGated && (
                <p className="mt-1 text-[11px] font-mono text-muted-foreground" data-testid="laser-condition-note">
                  New-condition IR lasers/illuminators aren't allowed (export / anti-diversion) — list it as used.
                </p>
              )}
              {form.itar && !laserIrGated && form.category !== NV_THERMAL_CAT && (
                <p className="mt-1 text-[11px] font-mono text-muted-foreground" data-testid="itar-condition-note">
                  ITAR-controlled items can't be listed New (export / anti-diversion) — list it as used.
                </p>
              )}
            </div>
            <div>
              <label className={labelCls}>Price ($)</label>
              <input type="number" min="0" step="0.01" value={hasVariants ? "" : form.price} onChange={set("price")} disabled={hasVariants} placeholder={hasVariants ? "Set per option below" : ""} className={`${inputCls} ${hasVariants ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="create-price" />
              {hasVariants && (
                <p className="mt-1 text-[11px] font-mono text-muted-foreground" data-testid="price-from-variants-note">
                  Buyers pick an option below — each has its own price.
                </p>
              )}
              {!hasVariants && feePreview && form.listing_type === "sell" && (
                <div className="mt-2 max-w-xs" data-testid="create-fee-preview">
                  <PayoutBreakdown
                    amountCents={feePreview.amount_cents}
                    shippingCents={feePreview.shipping_cents}
                    absorbedCents={feePreview.absorbed_cents}
                    platformFeeCents={feePreview.platform_fee_cents}
                    processingCents={feePreview.processing_cents}
                    payoutCents={feePreview.payout_cents}
                    ratePct={feePreview.rate_pct}
                    tierLabel={feePreview.tier_label}
                    tierMismatch={feePreview.tier_mismatch}
                    entitledLabel={feePreview.entitled_label}
                    shippingUnknown={absorbUnknown}
                    estimated
                    testId="create-payout-breakdown"
                  />
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>Shipping</label>
              <select value={form.shipping} onChange={set("shipping")} className={inputCls} data-testid="create-shipping">
                {SHIPPING.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          )}

          {/* AGE + WARRANTY — optional provenance, sell listings only. */}
          {form.listing_type !== "buy" && (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="create-provenance">
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label className={labelCls} style={{ marginBottom: 0 }}>Age <span className="text-muted-foreground normal-case">(optional)</span></label>
                  <div className="inline-flex border border-sniper text-[10px] font-mono uppercase" data-testid="create-age-mode">
                    <button type="button" onClick={() => setField("age_mode", "date")} data-testid="create-age-mode-date"
                      className={`px-2 py-0.5 transition-colors ${form.age_mode === "date" ? "bg-olive/20 text-foreground border-r border-olive/40" : "text-muted-foreground hover:text-foreground border-r border-sniper"}`}>
                      Purchase Date
                    </button>
                    <button type="button" onClick={() => setField("age_mode", "approx")} data-testid="create-age-mode-approx"
                      className={`px-2 py-0.5 transition-colors ${form.age_mode === "approx" ? "bg-olive/20 text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                      Approx. Age
                    </button>
                  </div>
                </div>
                {form.age_mode === "date" ? (
                  <input type="date" value={form.purchase_date} onChange={set("purchase_date")} max={new Date().toISOString().slice(0, 10)}
                    className={`${inputCls} mt-2`} data-testid="create-purchase-date" />
                ) : (
                  <input value={form.approx_age} onChange={set("approx_age")} placeholder="e.g. 2 years"
                    className={`${inputCls} mt-2`} data-testid="create-approx-age" />
                )}
              </div>
              <div>
                <label className={labelCls}>Warranty remaining <span className="text-muted-foreground normal-case">(optional)</span></label>
                <input value={form.warranty_remaining} onChange={set("warranty_remaining")} placeholder="e.g. 18 months left on manufacturer warranty, or None"
                  className={inputCls} data-testid="create-warranty" />
              </div>
            </div>
          )}

          {/* VARIANTS / OPTIONS — sell listings only. One listing holds multiple configs
              (reticle, tube size, condition), each with its own price + quantity. */}
          {form.listing_type === "sell" && (
            <div className="border border-sniper bg-obsidian/40 p-4" data-testid="create-variants-section">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <label className={labelCls}>Options / variants <span className="text-muted-foreground normal-case">(optional)</span></label>
                  <p className="mt-1 text-[11px] font-mono text-muted-foreground max-w-md">
                    Selling the same model in a few configs? Add each as an option instead of posting near-duplicate listings. Buyers pick one at checkout.
                  </p>
                </div>
              </div>
              {(form.variants || []).length > 0 && (
                <div className="mt-3 space-y-2" data-testid="variants-rows">
                  <div className="hidden sm:grid grid-cols-[1fr_100px_80px_130px_32px] gap-2 text-[10px] font-mono uppercase tracking-wide text-muted-foreground px-0.5">
                    <span>Label</span><span>Price ($)</span><span>Qty</span><span>Condition</span><span></span>
                  </div>
                  {(form.variants || []).map((v, i) => (
                    <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_100px_80px_130px_32px] gap-2 items-center" data-testid={`variant-row-${i}`}>
                      <input value={v.label} onChange={(e) => updVariant(i, "label", e.target.value)} placeholder="30mm tube, MOA" className={`${inputCls} col-span-2 sm:col-span-1`} data-testid={`variant-label-${i}`} />
                      <input type="number" min="0" step="0.01" value={v.price} onChange={(e) => updVariant(i, "price", e.target.value)} placeholder="Price" className={inputCls} data-testid={`variant-price-${i}`} />
                      <input type="number" min="1" step="1" value={v.quantity} onChange={(e) => updVariant(i, "quantity", e.target.value)} placeholder="Qty" className={inputCls} data-testid={`variant-qty-${i}`} />
                      <select value={v.condition || ""} onChange={(e) => updVariant(i, "condition", e.target.value)} className={inputCls} data-testid={`variant-condition-${i}`}>
                        <option value="">Same as listing</option>
                        {conditionOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <button type="button" onClick={() => rmVariant(i)} title="Remove option" data-testid={`variant-remove-${i}`}
                        className="flex items-center justify-center border border-sniper hover:border-blaze/60 text-muted-foreground hover:text-blaze py-2 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" onClick={addVariant} data-testid="variant-add-btn"
                className="mt-3 inline-flex items-center gap-2 border border-olive/60 hover:border-olive text-olive-bright font-mono uppercase text-xs tracking-wide px-3 py-2 transition-colors">
                <Plus className="w-4 h-4" /> Add an option
              </button>
            </div>
          )}

          {/* BEST OFFER — fixed-price sell listings. Enable haggling + optional auto-accept /
              auto-decline thresholds (the decline floor is private, buyers never see it). */}
          {form.listing_type === "sell" && (
            <div className="border border-sniper bg-obsidian/40 p-4" data-testid="create-offers-section">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={!!form.accept_offers} onChange={set("accept_offers")} className="accent-olive w-4 h-4" data-testid="create-accept-offers" />
                <span className="font-heading uppercase text-sm text-foreground">Accept offers <span className="font-body normal-case text-muted-foreground">— let buyers haggle (Best Offer)</span></span>
              </label>
              {form.accept_offers && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="offer-thresholds">
                  <div>
                    <label className={labelCls}>Auto-accept at / above ($) <span className="text-muted-foreground normal-case">optional</span></label>
                    <input type="number" min="0" step="0.01" value={form.offer_auto_accept} onChange={set("offer_auto_accept")} placeholder="e.g. 1900" className={inputCls} data-testid="create-offer-auto-accept" />
                    <p className="mt-1 text-[11px] font-mono text-muted-foreground">Offers this high are accepted instantly + reserve the listing.</p>
                  </div>
                  <div>
                    <label className={labelCls}>Auto-decline below ($) <span className="text-muted-foreground normal-case">optional</span></label>
                    <input type="number" min="0" step="0.01" value={form.offer_min} onChange={set("offer_min")} placeholder="e.g. 1500" className={inputCls} data-testid="create-offer-min" />
                    <p className="mt-1 text-[11px] font-mono text-muted-foreground">Offers below this are declined instantly. Buyers never see this number.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SHARE CARD PREVIEW — sell listings with a photo + title. Shows the exact 1200x630 card
              that renders when the listing link is pasted (iMessage/X/Facebook/Slack). */}
          {sharePreviewUrl && (
            <div className="border border-sniper bg-obsidian/40 p-4" data-testid="share-card-preview">
              <label className={labelCls}>Share card preview <span className="text-muted-foreground normal-case">— how your link looks when shared</span></label>
              <div className="mt-2 max-w-md border border-sniper bg-obsidian overflow-hidden" style={{ aspectRatio: "1200 / 630" }}>
                <img src={sharePreviewUrl} alt="Share card preview" className="w-full h-full object-cover" data-testid="share-card-preview-image" />
              </div>
            </div>
          )}

          {form.category !== "Services" && (
            <div className="grid sm:grid-cols-3 gap-4" data-testid="create-color-row">
              <div>
                <label className={labelCls}>Color / finish{prefilled.has("color") && <CatalogMark k="color" />}</label>
                <select value={form.color} onChange={set("color")} className={mk("color")} data-testid="create-color">
                  <option value="">—</option>
                  {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* PACKAGE & SHIPPING — required to publish a shippable sell listing. Hidden for pickup-only.
              BUY-A-LABEL is behind PLATFORM_LABELS_ENABLED (EasyPost pending): with it OFF the only
              path is own-label, so the buy-a-label control is ABSENT (not greyed) and we ask for a
              flat price + tracking-after-sale only — no presets, no dimensions we would never use. */}
          {shippable && (
            <div ref={packageRef} className="border border-sniper bg-surface p-4 space-y-4" data-testid="create-package-section" style={{ scrollMarginTop: "96px" }}>
              <div>
                <label className={labelCls}>{labelsOn ? "Packaging & shipping" : "Shipping cost to buyer"}</label>
                <p className="text-[12px] font-body text-secondary-foreground">
                  {labelsOn
                    ? "How this ships. We use it to price shipping for the buyer."
                    : "You ship it yourself. A tracking number is required after the sale — we release your payment once delivery is confirmed."}
                </p>
              </div>

              {labelsOn && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <button type="button" data-testid="ship-mode-buy" data-package-focus onClick={() => setShipMode("buy_label")}
                    className={`text-left border p-3 transition-colors ${form.ship_mode === "buy_label" ? "border-olive bg-olive/10" : "border-sniper hover:border-olive/50"}`}>
                    <div className="font-heading font-bold uppercase text-sm text-foreground flex items-center gap-2"><Package className="w-4 h-4" /> Buy a shipping label</div>
                    <p className="text-[11px] font-body text-secondary-foreground mt-1">We rate-shop UPS/USPS/FedEx and print a prepaid label. Enter the box size.</p>
                  </button>
                  <button type="button" data-testid="ship-mode-own" onClick={() => setShipMode("own_label")}
                    className={`text-left border p-3 transition-colors ${form.ship_mode === "own_label" ? "border-olive bg-olive/10" : "border-sniper hover:border-olive/50"}`}>
                    <div className="font-heading font-bold uppercase text-sm text-foreground flex items-center gap-2"><Truck className="w-4 h-4" /> I&apos;ll ship it myself</div>
                    <p className="text-[11px] font-body text-secondary-foreground mt-1">You buy your own label and add a tracking number after the sale. Set a flat shipping price.</p>
                  </button>
                </div>
              )}

              {labelsOn && effectiveMode === "buy_label" ? (
                <div className="space-y-3" data-testid="pkg-buy-fields">
                  <div>
                    <label className={labelCls}>Package preset</label>
                    <select value={form.package_preset} onChange={set("package_preset")} className={inputCls} data-testid="pkg-preset">
                      <option value="">Custom size…</option>
                      {presets.map((p) => <option key={p.key} value={p.key}>{p.label} — {p.length}×{p.width}×{p.height} in · {p.weight_lb} lb</option>)}
                    </select>
                  </div>
                  {!form.package_preset && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div><label className={labelCls}>Length (in)</label><input type="number" min="0" step="0.1" value={form.pkg_length_in} onChange={set("pkg_length_in")} className={inputCls} data-testid="pkg-length" /></div>
                        <div><label className={labelCls}>Width (in)</label><input type="number" min="0" step="0.1" value={form.pkg_width_in} onChange={set("pkg_width_in")} className={inputCls} data-testid="pkg-width" /></div>
                        <div><label className={labelCls}>Height (in)</label><input type="number" min="0" step="0.1" value={form.pkg_height_in} onChange={set("pkg_height_in")} className={inputCls} data-testid="pkg-height" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 max-w-xs">
                        <div><label className={labelCls}>Weight (lb)</label><input type="number" min="0" step="1" value={form.pkg_weight_lb} onChange={set("pkg_weight_lb")} className={inputCls} data-testid="pkg-weight-lb" /></div>
                        <div><label className={labelCls}>Weight (oz)</label><input type="number" min="0" max="15.9" step="0.1" value={form.pkg_weight_oz} onChange={set("pkg_weight_oz")} className={inputCls} data-testid="pkg-weight-oz" /></div>
                      </div>
                      <p className="text-[11px] font-mono text-muted-foreground">Include the box + padding, and round up — an under-declared package can cost you a carrier reweigh fee.</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-3" data-testid="pkg-own-fields">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="own-free-toggle-row">
                    <input type="checkbox" checked={form.free_shipping} onChange={set("free_shipping")} className="accent-olive w-4 h-4" data-testid="own-free-toggle" />
                    <span className="font-mono text-xs uppercase text-secondary-foreground">Free shipping (you absorb it)</span>
                  </label>
                  <div className="max-w-xs">
                    <label className={labelCls}>{form.free_shipping ? "Your shipping cost — you absorb this ($)" : "Shipping cost to buyer ($)"}</label>
                    <input type="number" min="0" step="0.01" value={form.flat_shipping} onChange={set("flat_shipping")} className={inputCls} data-testid="own-flat-shipping" data-package-focus />
                    {form.free_shipping && (
                      <p className="text-[11px] font-body text-muted-foreground mt-1">Buyers pay $0 shipping. We subtract this from your estimate so YOU RECEIVE is your real take-home — it is never charged to the buyer.</p>
                    )}
                  </div>
                  <div className="border-l-2 border-volt/40 bg-volt/5 px-3 py-2" data-testid="own-tracking-note">
                    <p className="text-[11px] font-body text-secondary-foreground leading-relaxed">
                      <AlertTriangle className="w-3.5 h-3.5 text-volt inline mr-1 -mt-0.5" />
                      You&apos;ll enter a <span className="text-foreground font-semibold">tracking number</span> after the sale. Payment releases only after the carrier confirms delivery — <span className="text-foreground font-semibold">no tracking, no payout</span>.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className={labelCls}>Description</label>
            <textarea rows={5} value={form.description} onChange={set("description")} className={inputCls} data-testid="create-description"
              placeholder="Condition details, round count, included accessories, reason for sale..." />
          </div>

          <div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className={labelCls}>Item location</label>
              {locPrefilled && !editing && (
                <span className="font-mono text-[10px] uppercase tracking-wide text-[#8FA84A]" data-testid="create-location-prefilled">
                  Prefilled from your profile — edit if this ships from elsewhere
                </span>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mt-1">
              <div>
                <label className={labelCls}>City</label>
                <input value={form.city} onChange={set("city")} className={inputCls} data-testid="create-city" />
              </div>
              <div>
                <label className={labelCls}>State</label>
                <select value={form.state} onChange={set("state")} className={inputCls} data-testid="create-state">
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Zip</label>
                <input value={form.zip_code} onChange={set("zip_code")} className={inputCls} data-testid="create-zip" />
              </div>
            </div>
          </div>

          <div className="border border-sniper bg-surface p-4 space-y-3">
            <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5 text-blaze" /> Compliance flags
            </p>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="create-itar">
              <input type="checkbox" checked={form.itar} onChange={set("itar")} className="accent-blaze w-4 h-4" />
              <span className="font-mono text-xs uppercase text-secondary-foreground">ITAR-controlled (export restricted)</span>
            </label>
            {form.itar && (
              <div className="border border-[#C2600F]/50 bg-[#C2600F]/10 text-[#E8C46A] px-4 py-3 flex items-start gap-2" data-testid="itar-gate-note">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="text-sm font-body">
                  Flagging ITAR routes this listing to a <span className="font-semibold">moderator</span> before it goes live, and it can only be listed as <span className="font-semibold">used</span> (not New). Uncheck it if this item isn&apos;t export-controlled.
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer" data-testid="create-ffl">
              <input type="checkbox" checked={form.requires_ffl} onChange={set("requires_ffl")} className="accent-blaze w-4 h-4" />
              <span className="font-mono text-xs uppercase text-secondary-foreground">Requires FFL transfer</span>
            </label>
          </div>

          {!editing && (
            <label className="flex items-start gap-3 border border-sniper bg-surface p-4 cursor-pointer" data-testid="create-attestation">
              <input type="checkbox" checked={form.attestation} onChange={set("attestation")} className="accent-olive w-4 h-4 mt-0.5 shrink-0" data-testid="create-attestation-checkbox" />
              <span className="font-body text-[13px] text-secondary-foreground leading-relaxed">
                I confirm I <span className="text-foreground font-semibold">own this item</span> and have the right to sell it, that my <span className="text-foreground font-semibold">description and photos are accurate</span>, and that this sale complies with all applicable laws and OpticSwap's seller terms.
              </span>
            </label>
          )}

          {/* OPEN TO TRADE — singular, sits immediately above the Post button (spec). Keeps the
              "what are you looking for" free-text field. The stored field name (open_to_trades)
              is unchanged, so the feed filter + saved-search facet keep working. */}
          <div className="border border-sniper bg-surface p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="create-open-trades">
              <input type="checkbox" checked={form.open_to_trades} onChange={set("open_to_trades")} className="accent-olive w-4 h-4" />
              <span className="font-mono text-xs uppercase text-secondary-foreground flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5" /> Open to trade</span>
            </label>
            {form.open_to_trades && (
              <input value={form.trade_preferences} onChange={set("trade_preferences")} placeholder="What are you looking for in trade?" className={inputCls} data-testid="create-trade-prefs" />
            )}
          </div>

          <div className="flex gap-3">
            {!editing && (
              <button type="button" onClick={saveDraft} disabled={savingDraft || busy} data-testid="create-save-draft"
                className="flex-none border border-sniper hover:border-olive disabled:opacity-50 disabled:cursor-not-allowed text-secondary-foreground hover:text-foreground font-heading font-bold uppercase text-sm px-5 py-3.5 transition-colors flex items-center justify-center gap-2">
                {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {draftId ? "Update draft" : "Save draft"}
              </button>
            )}
            <button type="submit" disabled={busy || emailUnverified || !!catalogBlock || needsLeaf || !!prohibitedBlock || (needsUppersAttest && !editing && !form.uppers_attest) || (!editing && !form.attestation)} data-testid="create-submit"
              className="flex-1 bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold uppercase text-lg py-3.5 transition-colors flex items-center justify-center gap-2">
              <Upload className="w-5 h-5" /> {emailUnverified ? "Verify email to publish" : busy ? "Posting..." : editing ? "Save Changes" : "Post Listing"}
            </button>
          </div>
          {!editing && (
            <p className="text-[11px] font-mono text-muted-foreground text-center -mt-2" data-testid="draft-hint">
              Save draft keeps this private on your dashboard — nothing publishes until you hit Post.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
